import { useCallback, useEffect } from 'react';

import { IconButton, Stack, Tooltip } from '@mui/material';

import { IcArrowLeft, IcArrowRight } from '@/assets/images';
import { useCFSNavigation, useCFSPrefetch } from '@/hooks';
import { useCFSNavigationStore } from '@/store';

interface CFSNavigationArrowsProps {
  cfsId: string;
}

export const CFSNavigationArrows = ({ cfsId }: CFSNavigationArrowsProps) => {
  const {
    canGoNext,
    canGoPrevious,
    goToNext,
    goToPrevious,
    hasNavigationContext,
  } = useCFSNavigation(cfsId);
  const { prefetch } = useCFSPrefetch();

  // Get next/prev IDs for prefetching
  const getNextCFSId = useCFSNavigationStore((state) => state.getNextCFSId);
  const getPreviousCFSId = useCFSNavigationStore(
    (state) => state.getPreviousCFSId,
  );

  // Prefetch both neighbors when component mounts (handles case where cursor is already on arrow)
  useEffect(() => {
    const nextId = getNextCFSId(cfsId);
    const prevId = getPreviousCFSId(cfsId);
    if (nextId) prefetch(nextId);
    if (prevId) prefetch(prevId);
  }, [cfsId, getNextCFSId, getPreviousCFSId, prefetch]);

  const handlePrevHover = useCallback(() => {
    const prevId = getPreviousCFSId(cfsId);
    if (prevId) prefetch(prevId);
  }, [cfsId, getPreviousCFSId, prefetch]);

  const handleNextHover = useCallback(() => {
    const nextId = getNextCFSId(cfsId);
    if (nextId) prefetch(nextId);
  }, [cfsId, getNextCFSId, prefetch]);

  if (!hasNavigationContext) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Tooltip title="Previous Record">
        <span>
          <IconButton
            size="small"
            disabled={!canGoNext}
            onClick={goToNext}
            onMouseEnter={handleNextHover}
            aria-label="Older Record"
            sx={{
              padding: '4px',
              '&:disabled': {
                opacity: 0.3,
              },
            }}
          >
            <IcArrowLeft />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Next Record">
        <span>
          <IconButton
            size="small"
            disabled={!canGoPrevious}
            onClick={goToPrevious}
            onMouseEnter={handlePrevHover}
            aria-label="Newer Record"
            sx={{
              padding: '4px',
              '&:disabled': {
                opacity: 0.3,
              },
            }}
          >
            <IcArrowRight />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};
